import React from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from 'semantic';
import { useSearchContext } from 'components/search/Context';
import { ProviderMSPLimitsTrustTiers } from 'types/provider';
import { Link } from 'react-router-dom';

export default function TrustTierTable() {
  const { t } = useTranslation();
  const { items, loading } = useSearchContext<ProviderMSPLimitsTrustTiers>();

  if (!items.length || loading) return null;

  const groupedRows = transformLevels(items);

  return (
    <Table celled structured>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>
            {t('mspLimits.trustTiersHeaders.name', 'Name')}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t('mspLimits.trustTiersHeaders.currency', 'Currency')}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t(
              'mspLimits.trustTiersHeaders.individualLimit',
              'Individual Limit'
            )}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t(
              'mspLimits.trustTiersHeaders.organizationLimit',
              'Organization Limit'
            )}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t(
              'mspLimits.trustTiersHeaders.numberOfAccounts',
              'No. of Accounts'
            )}
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {groupedRows.map((row: GroupedLevel) => {
          return row.currencies.map((data, index) => (
            <Table.Row key={`row-${row.level}-${data.currency}`}>
              {index === 0 && (
                <Table.Cell rowSpan={row.currencies.length}>
                  {t('mspLimits.trustTiers.TierName', 'Tier {{TIER_NUMBER}}', {
                    TIER_NUMBER: row.level,
                  })}{' '}
                  {row.level === 0 &&
                    `(${t('mspLimits.trustTiers.NoTrust', 'No Trust')})`}
                </Table.Cell>
              )}
              <Table.Cell>{data.currency}</Table.Cell>
              <Table.Cell>
                {data.individual.value === -1
                  ? t('mspLimits.trustTiers.Unlimited', 'Unlimited')
                  : data.individual.value}
              </Table.Cell>
              <Table.Cell>
                {data.organization.value === -1
                  ? t('mspLimits.trustTiers.Unlimited', 'Unlimited')
                  : data.organization.value}
              </Table.Cell>
              {index === 0 && (
                <Table.Cell rowSpan={row.currencies.length}>
                  {row.numberOfAccounts && row.numberOfAccounts > 0 ? (
                    <Link
                      key="account"
                      to={`/accounts?page=1&trustTiers=${row.level}`}
                      target="_blank">
                      {row.numberOfAccounts}
                    </Link>
                  ) : (
                    0
                  )}
                </Table.Cell>
              )}
            </Table.Row>
          ));
        })}
      </Table.Body>
    </Table>
  );
}

type GroupedCurrency = {
  currency: string;
  organization: { value: number };
  individual: { value: number };
};

type GroupedLevel = {
  level: number;
  currencies: GroupedCurrency[];
  numberOfAccounts?: number;
};

function transformLevels(data: ProviderMSPLimitsTrustTiers[]): GroupedLevel[] {
  return data.map(({ level, limits, numberOfAccounts }) => {
    const currencyMap: Record<string, GroupedCurrency> = {};

    for (const { currency, type, limit } of limits) {
      if (!currencyMap[currency]) {
        currencyMap[currency] = {
          currency,
          organization: { value: -1 },
          individual: { value: -1 },
        };
      }

      currencyMap[currency][type] = { value: limit };
    }

    return {
      level,
      currencies: Object.values(currencyMap),
      numberOfAccounts,
    };
  });
}
